import React from "react";
import { ApolloCache, FetchResult, gql } from "@apollo/client";
import {
	graphTypeNames,
	MessageGroup,
	MessageGroupCreateInput,
	MessageGroupCreateMutation,
	MessageGroupDeleteMutation,
	MessageGroupDetailDocument,
	MessageGroupDetailQuery,
	MessageGroupDetailQueryVariables,
	MessageGroupUpdateInput,
	MessageGroupUpdateMutation,
	useMessageGroupCreateMutation,
	useMessageGroupDeleteMutation,
	useMessageGroupUpdateMutation,
	User,
	UserGroup,
} from "../../../Utilities";
import * as MessageGroupFormConversion from "./conversion";
import * as MessageGroupFormValues from "./form-values";
import { customUpdateCacheForCreate, customUpdateCacheForDelete, customUpdateCacheForUpdate } from "./mutation-custom";

// custom
export * from "./mutation-addon";

/**
 * Returns a `create` function for the MessageGroup model. The `create` function translates the given
 * `formValues` to the GraphQL create input for the MessageGroup model.
 */
export function useCreate<K extends keyof MessageGroupFormValues.Create = keyof MessageGroupFormValues.Create>() {
	const create = useDirectCreate();

	return React.useCallback(
		async (formValues: MessageGroupFormValues.CreateSubset<K>) => {
			const input = MessageGroupFormConversion.toGQLCreateInput(formValues);
			return create(input);
		},
		[create],
	);
}

/**
 * Returns a `create` function for the MessageGroup model. The `create` function accepts a GraphQL
 * create input for the model.
 */
export function useDirectCreate() {
	const [createMessageGroup] = useMessageGroupCreateMutation();

	return React.useCallback(
		async (input: MessageGroupCreateInput) => {
			const updateCache = getUpdateCacheForCreate(input);

			const { data, errors } = await createMessageGroup({ variables: { input }, update: updateCache });

			return { data: data?.createMessageGroup ?? null, errors: errors ?? null };
		},
		[createMessageGroup],
	);
}

/**
 * Returns an `update` function for the MessageGroup model. The `update` function translates the given
 * `formValues` to the GraphQL update input for the MessageGroup model.
 *
 * @param id The ID of the instance to update.
 */
export function useUpdate<K extends keyof MessageGroupFormValues.Detail = keyof MessageGroupFormValues.Detail>(
	id: MessageGroup["id"],
) {
	const update = useDirectUpdate<K>(id);

	return React.useCallback(
		async (
			changedFormValues: Partial<MessageGroupFormValues.DetailSubset<K>>,
			initialFormValues: MessageGroupFormValues.DetailSubset<K>,
		) => {
			const input = MessageGroupFormConversion.toGQLUpdateInput(changedFormValues, initialFormValues);
			return update(input, initialFormValues);
		},
		[update],
	);
}

/**
 * Returns an `update` function for the MessageGroup model. The `update` function accepts a GraphQL
 * update input for the model. The `initialFormValues` argument of the returned function should include at least those
 * keys that will be updated by the mutation.
 *
 * @param id The ID of the instance to update.
 */
export function useDirectUpdate<K extends keyof MessageGroupFormValues.Detail = keyof MessageGroupFormValues.Detail>(
	id: MessageGroup["id"],
) {
	const [updateMessageGroup] = useMessageGroupUpdateMutation();

	return React.useCallback(
		async (input: MessageGroupUpdateInput, initialFormValues: MessageGroupFormValues.DetailSubset<K>) => {
			const updateCache = getUpdateCacheForUpdate(input, initialFormValues);

			const { data, errors } = await updateMessageGroup({ variables: { id, input }, update: updateCache });

			return { data: data?.updateMessageGroup ?? null, errors: errors ?? null };
		},
		[updateMessageGroup, id],
	);
}

/**
 * Returns a `del` function for the MessageGroup model.
 *
 * @param id The ID of the instance to delete.
 */
export function useDelete(id: MessageGroup["id"]) {
	const [deleteMessageGroup] = useMessageGroupDeleteMutation();

	return React.useCallback(async () => {
		const updateCache = getUpdateCacheForDelete(id);

		const { data, errors } = await deleteMessageGroup({ variables: { id }, update: updateCache });

		return { data: data?.deleteMessageGroup ?? false, errors: errors ?? null };
	}, [deleteMessageGroup, id]);
}

function getUpdateCacheForCreate(input: MessageGroupCreateInput) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<MessageGroupCreateMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		const createdObject = result.data?.createMessageGroup;
		if (!createdObject) {
			return;
		}

		cache.writeQuery<MessageGroupDetailQuery, MessageGroupDetailQueryVariables>({
			query: MessageGroupDetailDocument,
			data: { messageGroup: createdObject },
			variables: { id: createdObject.id },
		});

		if (input.userGroupIDs) {
			for (let i = 0; i < input.userGroupIDs.length; i++) {
				addToMessageGroupsOfUserGroupCache(cache, input.userGroupIDs[i], createdObject);
			}
		}

		if (input.userIDs) {
			for (let i = 0; i < input.userIDs.length; i++) {
				addToMessageGroupsOfUserCache(cache, input.userIDs[i], createdObject);
			}
		}

		cache.evict({ id: "ROOT_QUERY", fieldName: "messageGroupConnection" });

		if (input.messages && input.messages.length > 0) {
			cache.evict({ id: "ROOT_QUERY", fieldName: "messageConnection" });
		}

		customUpdateCacheForCreate(input, cache, result);
	};
}

function getUpdateCacheForUpdate<K extends keyof MessageGroupFormValues.Detail = keyof MessageGroupFormValues.Detail>(
	input: MessageGroupUpdateInput,
	initialFormValues: MessageGroupFormValues.DetailSubset<K>,
) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<MessageGroupUpdateMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		const updatedObject = result.data?.updateMessageGroup;
		if (!updatedObject) {
			return;
		}

		if (input.addUserGroupIDs) {
			for (let i = 0; i < input.addUserGroupIDs.length; i++) {
				addToMessageGroupsOfUserGroupCache(cache, input.addUserGroupIDs[i], updatedObject);
			}
		}
		if (input.removeUserGroupIDs) {
			for (let i = 0; i < input.removeUserGroupIDs.length; i++) {
				removeFromMessageGroupsOfUserGroupCache(cache, input.removeUserGroupIDs[i], updatedObject);
			}
		}

		if (input.addUserIDs) {
			for (let i = 0; i < input.addUserIDs.length; i++) {
				addToMessageGroupsOfUserCache(cache, input.addUserIDs[i], updatedObject);
			}
		}
		if (input.removeUserIDs) {
			for (let i = 0; i < input.removeUserIDs.length; i++) {
				removeFromMessageGroupsOfUserCache(cache, input.removeUserIDs[i], updatedObject);
			}
		}

		customUpdateCacheForUpdate(input, initialFormValues, cache, result);
	};
}

function getUpdateCacheForDelete(id: MessageGroup["id"]) {
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<MessageGroupDeleteMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		if (!result.data?.deleteMessageGroup) {
			return;
		}

		cache.evict({ id: cache.identify({ id, __typename: graphTypeNames.MessageGroup }) });
		cache.evict({ id: "ROOT_QUERY", fieldName: "messageGroupConnection" });
		cache.gc();

		customUpdateCacheForDelete(id, cache, result);
	};
}

// custom export
export function addToMessageGroupsOfUserGroupCache(
	cache: ApolloCache<any>,
	targetID: UserGroup["id"],
	object: Pick<MessageGroup, "id">,
) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.UserGroup }),
		fields: {
			messageGroups: (cachedValue) => [...cachedValue, objectRef],
		},
	});
}

function removeFromMessageGroupsOfUserGroupCache(
	cache: ApolloCache<any>,
	targetID: UserGroup["id"],
	object: Pick<MessageGroup, "id">,
) {
	const cacheID = cache.identify(object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.UserGroup }),
		fields: {
			messageGroups: (cachedValue) => cachedValue.filter((e: any) => e.__ref !== cacheID),
		},
	});
}

// custom export
export function addToMessageGroupsOfUserCache(
	cache: ApolloCache<any>,
	targetID: User["id"],
	object: Pick<MessageGroup, "id">,
) {
	const objectRef = toCacheRef(cache, object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.User }),
		fields: {
			messageGroups: (cachedValue) => [...cachedValue, objectRef],
		},
	});
}

function removeFromMessageGroupsOfUserCache(
	cache: ApolloCache<any>,
	targetID: User["id"],
	object: Pick<MessageGroup, "id">,
) {
	const cacheID = cache.identify(object);
	cache.modify({
		id: cache.identify({ id: targetID, __typename: graphTypeNames.User }),
		fields: {
			messageGroups: (cachedValue) => cachedValue.filter((e: any) => e.__ref !== cacheID),
		},
	});
}

function toCacheRef(cache: ApolloCache<any>, object: Pick<MessageGroup, "id">) {
	return (
		cache.writeFragment({
			fragment: gql`
				fragment MessageGroupRef on MessageGroup {
					id
				}
			`,
			data: object,
		}) ?? null
	);
}
